import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    ClickAwayListener,
    List,
    ListItemAvatar,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    Popper,
    Typography,
    ListItemIcon,
    Divider,
    Switch,
    Stack,
} from '@mui/material';

import { User, useSwitchUser } from '@travelity/api';
import { useAuth0 } from '@auth0/auth0-react';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { useMediaDevice } from '@travelity/ui';
import { useTranslation } from 'react-i18next';
import MainCard from '../../../../../components/main-card/main-card';
import Transitions from '../../../../../components/transitions/transitions';
import { useNavigateAbsolute } from '../../../../../hooks';
import { SwitchWorkspaceModal } from '../../../../../components/switch-workspace-modal/switch-workspace-modal';
import { OnboardingClasses } from '../../../../../onboarding';
import { LanguageSwitch } from '../../../../../components/language-switch';

const enableMultiLanguage = false;

function ProfileSection({
    user,
    setSuperUser,
}: {
    user?: User;
    setSuperUser: (v: boolean) => void;
}) {
    const theme = useTheme();
    const navigate = useNavigateAbsolute();
    const { user: authUser } = useAuth0();
    const { isMobile, isMobileTablet } = useMediaDevice();
    const { logout } = useAuth0();
    const { t } = useTranslation('common');

    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [open, setOpen] = useState<number>(0);
    /**
     * anchorRef is used on different components and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef<HTMLLIElement | null>(null);
    const handleLogout = async () => {
        logout({
            logoutParams: {
                returnTo: window.location.origin,
            },
        });
    };

    const handleClose = (
        event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent
    ) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as Node)
        ) {
            return;
        }
        setOpen(0);
    };

    const handleListItemClick = (
        event: React.MouseEvent<HTMLDivElement>,
        index: number,
        route = ''
    ) => {
        setSelectedIndex(index);
        handleClose(event);

        if (route && route !== '') {
            navigate(route);
        }
    };
    const handleToggle = (e: React.MouseEvent) => {
        setOpen(prevOpen =>
            prevOpen ? 0 : e.currentTarget?.clientWidth || 198
        );
    };

    const [switchWorkspaces, setSwitchWorkspaces] = useState(false);

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current && !open) {
            anchorRef.current?.focus();
        }

        prevOpen.current = open;
    }, [open]);

    const { mutate: switchWorkspace, isLoading: isSwitching } = useSwitchUser({
        onSuccess: () => {
            setSwitchWorkspaces(false);
            navigate(0);
        },
    });
    const onWorkspaceSwitch = useCallback((id: string) => {
        switchWorkspace({ id });
    }, []);

    const userCanBeSuperUser =
        user?.workspaces.selected.role === 'administrator' ||
        user?.workspaces.selected.role === 'owner';

    const showProfileSettings = !isMobile;
    const showAppSettings = !isMobile;

    return (
        <>
            <ListItem
                sx={{
                    width: 'auto',
                    cursor: 'pointer',
                    pl: 1.5,
                    borderRadius: '12px',
                    py: 0,
                    maxWidth: '300px',
                    border: 'transparent 1px solid',
                    ...(open && {
                        border: '#DFE1ED 1px solid',
                        boxShadow: '0px 0px 10px 0px rgba(178, 185, 205, 0.40)',
                    }),
                }}
                ref={anchorRef}
                aria-controls={open ? 'nav-item-grow' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
            >
                <ListItemAvatar>
                    <Avatar
                        alt={authUser?.name}
                        sx={{
                            width: 40,
                            height: 40,
                            fontSize: '14px',
                            bgcolor: open ? '#7682A4' : '#B2B9CD',
                        }}
                        color="inherit"
                        src={user?.avatar}
                    >
                        <PersonIcon />
                    </Avatar>
                </ListItemAvatar>
                {!isMobileTablet && (
                    <ListItemText
                        title={
                            user?.fullName ||
                            authUser?.nickname ||
                            authUser?.name
                        }
                        secondaryTypographyProps={{
                            className: OnboardingClasses.WORKSPACE_TAG,
                            sx: {
                                borderRadius: '12px',
                                background: user?.superUser
                                    ? '#EC8031'
                                    : '#9498AC',
                                transition: 'background-color 0.3s ease',
                                color: '#FFF',
                                padding: '2px 8px',
                                display: 'inline-block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                            },
                        }}
                        sx={{
                            my: '2px',
                            '& .MuiListItemText-primary': {
                                color: '#2B395B',
                                fontWeight: 600,
                                pl: '2px',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                            },
                        }}
                        primary={
                            user?.fullName ||
                            authUser?.nickname ||
                            authUser?.name
                        }
                        secondary={
                            user?.workspaces.selected.name || authUser?.email
                        }
                    />
                )}
                <ExpandMoreIcon />
            </ListItem>

            <Popper
                placement="bottom-end"
                open={!!open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 4],
                            },
                        },
                    ],
                }}
            >
                {({ TransitionProps }) => (
                    <Transitions in={open} {...TransitionProps}>
                        <Paper
                            sx={{
                                borderRadius: '12px',
                                border: '1px solid #DFE1ED',
                                background: '#FFF',
                                minWidth: open,
                                boxShadow:
                                    '0px 0px 10px 0px rgba(178, 185, 205, 0.40)',
                            }}
                        >
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard
                                    border={false}
                                    elevation={16}
                                    content={false}
                                    boxShadow
                                    shadow={theme.shadows[16]}
                                >
                                    <Box>
                                        <List
                                            component="nav"
                                            sx={{
                                                width: '100%',
                                                backgroundColor:
                                                    theme.palette.background
                                                        .paper,
                                                borderRadius: '10px',
                                                [theme.breakpoints.down('md')]:
                                                    {
                                                        minWidth: '100%',
                                                    },
                                                '& .MuiListItemButton-root': {
                                                    mt: 0.5,
                                                },
                                            }}
                                        >
                                            {/* <Box> */}
                                            {/*    {user.workspaces.selected.role} */}
                                            {/* </Box> */}
                                            <ListItemButton
                                                sx={{
                                                    margin: '4px 16px',
                                                    borderRadius: '8px',
                                                }}
                                                selected={selectedIndex === 1}
                                                disabled={
                                                    user &&
                                                    user.workspaces.list
                                                        .length < 2
                                                }
                                                onClick={() => {
                                                    setSwitchWorkspaces(true);
                                                    setOpen(0);
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        minWidth: 'auto',
                                                        mr: 1,
                                                    }}
                                                >
                                                    <SyncAltIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    sx={{
                                                        color: '#7682A4',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                    primary={
                                                        <Typography variant="body2">
                                                            {t(
                                                                'Switch workspace'
                                                            )}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItemButton>
                                            {enableMultiLanguage && (
                                                <LanguageSwitch />
                                            )}
                                            <Divider sx={{ mx: 2 }} />
                                            {showProfileSettings && (
                                                <ListItemButton
                                                    sx={{
                                                        margin: '4px 16px',
                                                        borderRadius: '8px',
                                                    }}
                                                    selected={
                                                        selectedIndex === 2
                                                    }
                                                    onClick={e =>
                                                        handleListItemClick(
                                                            e,
                                                            2,
                                                            '/me/personal-details'
                                                        )
                                                    }
                                                >
                                                    <ListItemText
                                                        sx={{
                                                            color: '#7682A4',
                                                        }}
                                                        primary={
                                                            <Typography variant="body2">
                                                                {t(
                                                                    'Profile Settings'
                                                                )}
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItemButton>
                                            )}
                                            {showAppSettings && (
                                                <ListItemButton
                                                    sx={{
                                                        margin: '4px 16px',
                                                        borderRadius: '8px',
                                                    }}
                                                    selected={
                                                        selectedIndex === 3
                                                    }
                                                    onClick={e =>
                                                        handleListItemClick(
                                                            e,
                                                            3,
                                                            '/me/settings'
                                                        )
                                                    }
                                                >
                                                    <ListItemText
                                                        sx={{
                                                            color: '#7682A4',
                                                        }}
                                                        primary={
                                                            <Typography variant="body2">
                                                                {t(
                                                                    'App Settings'
                                                                )}
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItemButton>
                                            )}
                                            <ListItemButton
                                                sx={{
                                                    margin: '4px 16px',
                                                    borderRadius: '8px',
                                                }}
                                                selected={selectedIndex === 4}
                                                onClick={handleLogout}
                                            >
                                                <ListItemText
                                                    sx={{ color: '#7682A4' }}
                                                    primary={
                                                        <Typography variant="body2">
                                                            {t('Logout')}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItemButton>
                                            {userCanBeSuperUser && (
                                                <>
                                                    <Divider
                                                        sx={{ mx: 2, mb: 1 }}
                                                    />
                                                    <Stack
                                                        sx={{
                                                            pl: 4,
                                                            fontSize: '14px',
                                                            color: '#7682A4',
                                                        }}
                                                        alignItems="center"
                                                        direction="row"
                                                    >
                                                        {t('Super user mode')}
                                                        <Switch
                                                            color="warning"
                                                            checked={
                                                                !!user?.superUser
                                                            }
                                                            onChange={() =>
                                                                setSuperUser(
                                                                    !user?.superUser
                                                                )
                                                            }
                                                            sx={{
                                                                ml: 1,
                                                                '&& .Mui-checked+.MuiSwitch-track':
                                                                    {
                                                                        backgroundColor:
                                                                            '#EC8031',
                                                                    },
                                                                '&& .MuiSwitch-track':
                                                                    {
                                                                        '&:before':
                                                                            {
                                                                                left: '10px',
                                                                                content: `"${t(
                                                                                    'On'
                                                                                )}"`,
                                                                            },
                                                                        '&:after':
                                                                            {
                                                                                right: '12px',
                                                                                content: `"${t(
                                                                                    'Off'
                                                                                )}"`,
                                                                            },
                                                                    },
                                                            }}
                                                        />
                                                    </Stack>
                                                </>
                                            )}
                                        </List>
                                    </Box>
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
            {!!user && switchWorkspaces && (
                <SwitchWorkspaceModal
                    selected={user.workspaces.selected}
                    list={user.workspaces.list}
                    isSwitching={isSwitching}
                    onClose={() => setSwitchWorkspaces(false)}
                    onSwitch={onWorkspaceSwitch}
                />
            )}
        </>
    );
}

export default React.memo(ProfileSection);
